import { collection, getDocs, query, orderBy, limit, Timestamp } from "firebase/firestore";
import { getDb } from "@/lib/firebaseClient";
import { callAuthedFunction } from "@/lib/firebaseClient";
import type { BreweryRole } from "@/lib/useAuth";

/**
 * Registro de accesos (seguridad) — Opción A.
 * `logLoginEvent` avisa al servidor de un login exitoso; `listLoginEvents`
 * lee los últimos para el panel "Accesos recientes" (owner/admin).
 */

export type LoginEvent = {
  id: string;
  uid: string;
  email: string | null;
  displayName: string | null;
  role: BreweryRole | null;
  ip: string | null;
  userAgent: string | null;
  method: string | null;
  at: Timestamp | null;
};

/**
 * Registra un acceso. Se llama JUSTO después de un login exitoso (no en cada
 * refresh). Es fire-and-forget: nunca debe romper ni demorar el ingreso, así
 * que traga cualquier error (el claim `breweryId` podría no estar listo, no
 * pasa nada — el próximo login lo registra).
 */
export async function logLoginEvent(method: "google" | "password" | string): Promise<void> {
  try {
    await callAuthedFunction("logLogin", { method });
  } catch (e) {
    // Silencioso a propósito: el registro de acceso es best-effort.
    console.debug("[access] no se pudo registrar el acceso:", e);
  }
}

/** Últimos accesos, más recientes primero. Solo owner/admin pueden leerlos (reglas). */
export async function listLoginEvents(breweryId: string, max = 25): Promise<LoginEvent[]> {
  const db = getDb();
  if (!db) return [];
  const snap = await getDocs(
    query(
      collection(db, "breweries", breweryId, "loginEvents"),
      orderBy("at", "desc"),
      limit(max)
    )
  );
  return snap.docs.map((d) => {
    const data = d.data();
    return {
      id: d.id,
      uid: data.uid ?? "",
      email: data.email ?? null,
      displayName: data.displayName ?? null,
      role: (data.role as BreweryRole) ?? null,
      ip: data.ip ?? null,
      userAgent: data.userAgent ?? null,
      method: data.method ?? null,
      at: data.at ?? null,
    };
  });
}
